import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  SplitFeature,
  StatStripe,
  CinematicCTA,
  CinematicFAQ,
} from "@/components/service/Cinematic";
import heroImg from "@/assets/special-surface.jpg";
import surface1 from "@/assets/surface-ref-1.webp";
import surface2 from "@/assets/surface-ref-2.webp";
import surface3 from "@/assets/surface-ref-3.jpg";
import int1 from "@/assets/interior-ref-1.jpg";
import int2 from "@/assets/interior-ref-2.jpeg";
import int3 from "@/assets/interior-ref-3.jpeg";

const materials = [
  {
    title: "Calciumsilikat",
    subtitle: "Der Klassiker gegen Schimmel",
    text: "Hochporig und kapillaraktiv. Nimmt überschüssige Feuchte auf und gibt sie wieder ab – ohne selbst zu schimmeln. Ideal für Außenwände im Altbau, Ecken und Kellerdecken.",
    tags: ["Schimmelresistent", "Feuchtigkeitsregulierend", "Diffusionsoffen", "60–100 mm Aufbau"],
  },
  {
    title: "Holzfaser",
    subtitle: "Ökologisch & raumklimaoptimierend",
    text: "Natürlich, diffusionsoffen, mit hervorragender Wärmespeicherkapazität. Reguliert das Raumklima und bringt zusätzlichen Schallschutz mit.",
    tags: ["Ökologisch", "Schallschutz", "Wärmespeicher", "Gutes Raumklima"],
  },
  {
    title: "PUR / PIR",
    subtitle: "Maximalleistung bei wenig Platz",
    text: "Niedrigste Wärmeleitfähigkeit (WLS 022–026). Wenn Platz knapp ist – kleine Räume, Dachschrägen – liefert PIR auf wenigen Zentimetern maximale Wirkung.",
    tags: ["Höchste Dämmleistung", "Geringe Aufbauhöhe", "Ideal für Kellerdecken"],
  },
  {
    title: "Mineralwolle",
    subtitle: "Nicht brennbar & schalldämmend",
    text: "Steinwolle erfüllt Brandschutzklasse A1 und bietet hervorragenden Schallschutz. Ideal in Mehrfamilienhäusern oder bei strengen Brandschutzanforderungen.",
    tags: ["A1 nicht brennbar", "Schallschutz", "Bewährt", "Wirtschaftlich"],
  },
  {
    title: "HECK INNEO",
    subtitle: "Premium-Innendämmsystem aus Steinwolle",
    text: "Hoch diffusionsoffenes System mit Comfortrock ID Steinwolle (λ ≈ 0,035 W/mK). Vollflächige Verklebung – ohne Unterkonstruktion und ohne Dampfbremse. Die Alternative, wenn eine Außendämmung nicht möglich ist: historische Fassaden, Denkmalschutz, Eigentümergemeinschaften.",
    tags: ["A1 nicht brennbar", "Diffusionsoffen", "Ohne Dampfbremse", "30 / 60 mm Aufbau"],
  },
];

const ursachen = [
  { num: "01", title: "Kältebrücken", desc: "Ecken, Rollladenkästen, Balkonplatten – Bauteilanschlüsse, die die Dämmebene durchbrechen und Wärme nach außen ziehen." },
  { num: "02", title: "Unzureichende Dämmung", desc: "Altbauwände ohne Dämmkern lassen Wärme ungehindert entweichen und kühlen innen aus. Feuchtigkeit kondensiert an der kalten Wand." },
  { num: "03", title: "Falsche Materialwahl", desc: "Dampfdichte Farben auf kapillarer Wand blockieren den Feuchtetransport – Schimmel hat ideale Bedingungen." },
];

const faqs = [
  { q: "Verliere ich durch Innendämmung viel Wohnfläche?", a: "Calciumsilikat und Holzfaser benötigen 6–12 cm. PUR/PIR reichen 4–8 cm. In normalen Wohnräumen kaum wahrnehmbar – besonders, wenn nur eine Außenwand betroffen ist." },
  { q: "Kann Innendämmung Schimmel dauerhaft verhindern?", a: "Ja – wenn das richtige Material gewählt und korrekt verarbeitet wird. Calciumsilikat verhindert Tauwasserbildung. Wir analysieren das Gebäude vorher thermografisch und schließen alle Schwachstellen." },
  { q: "Gibt es Förderung für Innendämmung?", a: "Ja. Über die BEG Einzelmaßnahmen (KfW / BAFA), wenn ein vorgegebener U-Wert erreicht wird. Wir arbeiten mit qualifizierten Energieberatern zusammen und koordinieren den Antrag." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Innendämmung & Schimmelschutz",
  name: "Innendämmung & Schimmelschutz Herford",
  provider: { "@type": "LocalBusiness", name: "Malerfachbetrieb Stein GmbH & Co. KG" },
  areaServed: "Herford",
  description: "Innendämmung gegen Schimmel in Herford – Calciumsilikat, Holzfaser, PUR. Ursachen beseitigen, Raumklima verbessern.",
};

const InnendaemmungSchimmelschutzHerford = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Innendämmung in Herford: Schluss mit Schimmel & Kälte"
      description="Innendämmung & Schimmelschutz in Herford: Calciumsilikat, Holzfaser & PUR – dauerhaft trockene Wände, gesundes Raumklima, geringere Heizkosten. ☎ 05221 3832130"
      keywords="Innendämmung Herford, Schimmelschutz Herford, Schimmelsanierung Herford, Calciumsilikatplatten Herford, Holzfaserdämmung Herford, gesundes Raumklima, kalte Wände dämmen"
      canonical="https://malerfachbetrieb-stein.de/innendaemmung-schimmelschutz-herford"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 03 / Innendämmung"
        eyebrow="Innendämmung & Schimmelschutz"
        title={<>Kalte Wände sind <em className="not-italic text-background/70">kein</em> Lüftungsproblem.</>}
        subtitle="Wir lösen die Ursache. Mit Materialien, die Feuchtigkeit regulieren, statt sie zu blockieren – und einer Ausführung, die Schwachstellen lückenlos schließt."
        image={heroImg}
        imageAlt="Innendämmung & Schimmelschutz Herford"
        ctaLabel="Beratungstermin anfragen"
      />

      <StatStripe
        stats={[
          { value: "4", label: "Material­systeme" },
          { value: "100 %", label: "Ursache statt Symptom" },
          { value: "10+", label: "Jahre Erfahrung" },
          { value: "24h", label: "Rückmeldung" },
        ]}
      />

      {/* Problem */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto max-w-6xl">
          <SectionHead
            eyebrow="Die Ursache verstehen"
            title={<>Schimmel ist kein Reinigungs­problem – es ist ein Bauteil­problem.</>}
            intro="Schimmel entsteht fast immer an Kältebrücken. Die Wandoberfläche kühlt so stark ab, dass Raumfeuchte kondensiert. Schimmelfarbe übertüncht das Symptom. Wir beseitigen die Ursache."
          />
          <div className="grid md:grid-cols-3 gap-px bg-foreground/10">
            {ursachen.map((u, i) => (
              <Reveal key={u.num} delay={i * 80}>
                <div className="bg-background p-8 md:p-10 h-full">
                  <div className="font-caslon text-6xl font-bold text-foreground/12 mb-4">{u.num}</div>
                  <h3 className="font-caslon text-xl font-bold mb-3">{u.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{u.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FullBleedQuote
        image={surface1}
        imageAlt="Detail Innendämmung Calciumsilikat"
        quote={<>„Eine Wand muss atmen dürfen – sonst atmet der Schimmel an ihrer Stelle.“</>}
        caption="Praxis Innendämmung"
      />

      {/* Materialien */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Materialien"
            title="Vier Systeme – eine richtige Wahl."
          />
          <div className="grid md:grid-cols-2 gap-px bg-background/10">
            {materials.map((mat, i) => (
              <Reveal key={mat.title} delay={i * 80}>
                <div className="bg-foreground p-8 md:p-10 h-full">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-background/80 mb-3">
                    {String(i + 1).padStart(2, "0")} / {mat.subtitle}
                  </div>
                  <h3 className="font-caslon text-3xl font-bold mb-4">{mat.title}</h3>
                  <p className="text-background/70 leading-relaxed text-sm mb-6">{mat.text}</p>
                  <div className="flex flex-wrap gap-2">
                    {mat.tags.map((t) => (
                      <span key={t} className="text-[11px] px-3 py-1 border border-background/20 rounded-full text-background/80">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Anwendungen split */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Anwendungsbereiche"
            title="Wo wir in Herford ansetzen."
          />
          <div className="space-y-24 md:space-y-32">
            <SplitFeature
              index="01"
              eyebrow="Innenwände"
              title="Außenwände mit unzureichender Dämmung."
              body="Typisch im Altbau, im Denkmalschutz oder in Eigentümergemeinschaften ohne Möglichkeit zur Außendämmung. Wir nehmen den Raum, der vorhanden ist, und arbeiten anschlussdicht."
              image={int1}
              imageAlt="Innendämmung im Altbau"
            />
            <SplitFeature
              reverse
              index="02"
              eyebrow="Kellerdecken & Dachboden"
              title="Die schnellen Hebel mit großer Wirkung."
              body="Eine gedämmte Kellerdecke verhindert, dass kalte Kellerluft den Wohnraum kühlt. Eine Dachboden­dämmung – ob ausgelegt oder eingeblasen – ist die günstigste Maßnahme mit messbarer Wirkung."
              image={int2}
              imageAlt="Kellerdecken- und Dachbodendämmung"
            />
            <SplitFeature
              index="03"
              eyebrow="Dachgeschossausbau"
              title="Bewohntes Dach – maximale Wirkung."
              body="Kombination aus Zwischen- und Untersparrendämmung maximiert die Energieeinsparung bei minimalem Raumverlust. Ideal beim Ausbau ehemals ungenutzter Geschosse."
              image={int3}
              imageAlt="Dachgeschossausbau mit Dämmung"
            />
            <SplitFeature
              reverse
              index="04"
              eyebrow="HECK INNEO – Premium-System"
              title="Wenn Außendämmung nicht geht: das System unserer Wahl."
              body="HECK INNEO ist ein hoch diffusionsoffenes Innendämmsystem auf Basis der Steinwolle-Platte Comfortrock ID (λ ≈ 0,035 W/mK, Brandklasse A1). Vollflächig verklebt – ohne Unterkonstruktion und ohne klassische Dampfbremse. Kapillaraktiv und atmungsaktiv: Feuchte wird transportiert statt eingeschlossen, das Risiko von Kondensat und Schimmel sinkt deutlich. Damit ist HECK INNEO unsere Empfehlung überall dort, wo eine Außendämmung nicht möglich oder gewünscht ist – etwa bei historischen Fassaden, im Denkmalschutz oder in Eigentümergemeinschaften."
              bullets={[
                "λ ≈ 0,035 W/mK – starke Dämmleistung",
                "Nicht brennbar (Steinwolle, A1)",
                "Ohne Unterkonstruktion & Dampfbremse",
                "Aufbau 30 oder 60 mm – wenig Raumverlust",
                "Ideal für Altbau, Denkmal & WEG",
              ]}
              image={surface2}
              imageAlt="HECK INNEO Innendämmsystem mit Steinwolle Comfortrock ID"
            />
          </div>
        </div>
      </section>

      <CinematicFAQ
        faqs={faqs}
        title="Fragen, die uns oft erreichen."
      />

      <CinematicCTA
        image={surface3}
        imageAlt="Innendämmung Detail"
        title={<>Schimmel? Kalte Wände?<br /> Wir lösen das dauerhaft.</>}
        body="Kostenlose Vor-Ort-Analyse. Wir zeigen Ihnen die Ursache – und die passende Lösung."
        ctaLabel="Termin anfragen"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default InnendaemmungSchimmelschutzHerford;